import type {
  InventoryStockLocationRecord,
  InventoryUnitRecord,
  SaveStockTransferRequest,
  StockTransferRecord,
} from '@/renderer/types/inventory';
import { Button, Modal } from '@components/common';
import {
  useInventoryItemSelectOptions,
  useInventoryStockBalances,
} from '@hooks/inventory';
import React, { useEffect, useId, useMemo, useState } from 'react';
import { StockTransferDetailsFields } from './StockTransferDetailsFields';
import {
  StockTransferFormContext,
  StockTransferFormContextValue,
} from './StockTransferFormContext';
import { StockTransferLineItemsSection } from './StockTransferLineItemsSection';
import { StockTransferReadOnlyView } from './StockTransferReadOnlyView';
import {
  buildInitialStockTransferFormState,
  buildSaveStockTransferRequest,
  canSubmitStockTransferForm,
  createEmptyStockTransferLine,
  FormState,
  hasAnyInvalidQuantity as computeHasAnyInvalidQuantity,
  hasAnyOverAvailable as computeHasAnyOverAvailable,
  LineForm,
} from './stockTransferFormUtils';

interface Props {
  transfer: StockTransferRecord | null;
  isLoadingDetail?: boolean;
  locations: InventoryStockLocationRecord[];
  units: InventoryUnitRecord[];
  companyId: number;
  outletId: string;
  isSaving: boolean;
  onSave: (req: SaveStockTransferRequest) => void;
  onClose: () => void;
}

export const StockTransferEditorModal: React.FC<Props> = ({
  transfer,
  isLoadingDetail = false,
  locations,
  units,
  companyId,
  outletId,
  isSaving,
  onSave,
  onClose,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const uid = useId();
  const [form, setForm] = useState<FormState>(() =>
    buildInitialStockTransferFormState(transfer, today),
  );

  const isReadOnly = Boolean(transfer && transfer.status !== 'draft');

  const selectedItemIds = form.lines
    .map((l) => l.inventoryItemId)
    .filter(Boolean);
  const itemOptions = useInventoryItemSelectOptions({
    companyId,
    status: 'active',
    pageSize: 10,
    selectedItemIds,
  });

  const dedupedItemIds = Array.from(new Set(selectedItemIds));
  const balancesQuery = useInventoryStockBalances(
    {
      companyId,
      outletId,
      stockLocationId: form.fromLocationId,
      inventoryItemIds: dedupedItemIds,
      page: 1,
      pageSize: Math.max(dedupedItemIds.length, 1),
    },
    {
      enabled:
        !isReadOnly && !!form.fromLocationId && dedupedItemIds.length > 0,
    },
  );
  const balanceByItemId = new Map(
    (balancesQuery.data?.data ?? []).map((b) => [
      b.inventory_item_id,
      b.quantity_on_hand,
    ]),
  );

  useEffect(() => {
    setForm(buildInitialStockTransferFormState(transfer, today));
    // today is stable within a render session — intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transfer]);

  const setFormField = <K extends keyof Omit<FormState, 'lines'>>(
    field: K,
    value: FormState[K],
  ) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const setLine = (
    key: string,
    field: keyof Omit<LineForm, 'key'>,
    value: string,
  ) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((l) =>
        l.key === key ? { ...l, [field]: value } : l,
      ),
    }));
  };

  const addLine = () =>
    setForm((prev) => ({
      ...prev,
      lines: [...prev.lines, createEmptyStockTransferLine()],
    }));

  const removeLine = (key: string) =>
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((l) => l.key !== key),
    }));

  const hasAnyInvalidQuantity = computeHasAnyInvalidQuantity(form.lines);
  const hasAnyOverAvailable = computeHasAnyOverAvailable(
    form.lines,
    balanceByItemId,
    form.fromLocationId,
  );
  const canSubmit = canSubmitStockTransferForm(form, balanceByItemId);

  const handleSubmit = () => {
    onSave(
      buildSaveStockTransferRequest(form, {
        transferId: transfer?.id,
        companyId,
        outletId,
      }),
    );
  };

  const contextValue: StockTransferFormContextValue = useMemo(
    () => ({
      locations,
      units,
      isSaving,
      onClose,
      uid,
      form,
      setFormField,
      setLine,
      addLine,
      removeLine,
      itemOptions,
      balanceByItemId,
      isBalancesFetching: balancesQuery.isFetching,
      isBalancesLoading: balancesQuery.isLoading,
      hasAnyInvalidQuantity,
      hasAnyOverAvailable,
      canSubmit,
      handleSubmit,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      locations,
      units,
      isSaving,
      onClose,
      uid,
      form,
      itemOptions,
      balanceByItemId,
      balancesQuery.isFetching,
      balancesQuery.isLoading,
      hasAnyInvalidQuantity,
      hasAnyOverAvailable,
      canSubmit,
    ],
  );

  if (isReadOnly && transfer) {
    return (
      <StockTransferReadOnlyView
        transfer={transfer}
        locations={locations}
        units={units}
        isLoadingDetail={isLoadingDetail}
        onClose={onClose}
      />
    );
  }

  return (
    <StockTransferFormContext.Provider value={contextValue}>
      <Modal
        isOpen
        onClose={onClose}
        title={transfer ? 'Edit Transfer' : 'New Stock Transfer'}
        size="xl"
      >
        <div className="space-y-4">
          <StockTransferDetailsFields />
          <StockTransferLineItemsSection />

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="text" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={!canSubmit || isSaving}
              isLoading={isSaving}
            >
              {transfer ? 'Save Changes' : 'Create Transfer'}
            </Button>
          </div>
        </div>
      </Modal>
    </StockTransferFormContext.Provider>
  );
};
